import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const NotificationAlertSchema = z
    .object({
        id: zIdSchema,
        title: z.string().trim(),
        body: z.string().trim(),
        read: z.boolean(),
        createdAt: z.string().trim(),
    })
    .brand<"NotificationAlert">();

export type NotificationAlertInputProps = z.input<
    typeof NotificationAlertSchema
>;
export type NotificationAlertProps = z.output<typeof NotificationAlertSchema>;

export class NotificationAlert {
    private constructor(
        private readonly props: Readonly<NotificationAlertProps>,
    ) {}

    get id(): Id {
        return this.props.id;
    }

    get title(): string {
        return this.props.title;
    }

    get body(): string {
        return this.props.body;
    }

    get read(): boolean {
        return this.props.read;
    }

    get createdAt(): string {
        return this.props.createdAt;
    }

    static validate(
        props: Readonly<MaybeValidProps<NotificationAlertInputProps>>,
    ): Result<NotificationAlertProps, z.ZodError<NotificationAlertProps>> {
        const result = NotificationAlertSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<NotificationAlertInputProps>>,
    ): Result<NotificationAlertProps, PartialValid<typeof NotificationAlert>> {
        const result = partialSafeParse(NotificationAlertSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<NotificationAlertInputProps>,
    ): Result<NotificationAlert, z.ZodError<NotificationAlertProps>> {
        return NotificationAlert.validate(props).map(
            (validatedProps) => new NotificationAlert(validatedProps),
        );
    }

    static createWithRecovery(
        props: NotificationAlertInputProps,
    ): Result<NotificationAlert, ModelError<typeof NotificationAlert>> {
        return NotificationAlert.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<NotificationAlertProps> = {
                    id: error.validProps.id,
                    title: "",
                    body: "",
                    read: false,
                    createdAt: "",
                };

                const recoveryProps: Unbrand<NotificationAlertProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof NotificationAlert>(
                    new NotificationAlert(
                        recoveryProps as NotificationAlertProps,
                    ),
                    error.errors,
                );
            })
            .map((validatedProps) => new NotificationAlert(validatedProps));
    }

    toCreateProps(): NotificationAlertInputProps {
        return {
            id: this.props.id.toCreateProps(),
            title: this.props.title,
            body: this.props.body,
            read: this.props.read,
            createdAt: this.props.createdAt,
        };
    }
}

const NotificationAlertsSchema = z
    .object({
        notificationAlerts: z.array(
            z.custom<NotificationAlert>(
                (val) => val instanceof NotificationAlert,
                { error: "Invalid NotificationAlert instance" },
            ),
        ),
        count: z.number().min(0),
    })
    .brand<"NotificationAlerts">();

export type NotificationAlertsInputProps = z.input<
    typeof NotificationAlertsSchema
>;
export type NotificationAlertsProps = z.output<typeof NotificationAlertsSchema>;

export class NotificationAlerts {
    private constructor(
        private readonly props: Readonly<NotificationAlertsProps>,
    ) {}

    get notificationAlerts(): readonly Readonly<NotificationAlert>[] {
        return this.props.notificationAlerts;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): NotificationAlerts {
        return NotificationAlerts.create({
            notificationAlerts: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<NotificationAlertsInputProps>>,
    ): Result<NotificationAlertsProps, z.ZodError<NotificationAlertsProps>> {
        const result = NotificationAlertsSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<NotificationAlertsInputProps>,
    ): Result<NotificationAlerts, z.ZodError<NotificationAlertsProps>> {
        return NotificationAlerts.validate(props).map(
            (validatedProps) => new NotificationAlerts(validatedProps),
        );
    }
}

export interface NotificationAlertUpdate {
    id: number;
    read: boolean;
}
